import { Dispatch } from "redux";
import * as ChatActions from "./chat.actions";
import * as GlobalThunks from "../layout/layout.thunks";
import { ChatService } from "../../services/chatService";
import { RootState } from "../../globals/redux/store/store.index";
import { ChatHistoryService } from "../../services/chatHistoryService";
import { v4 as uuidv4 } from 'uuid';
import ReconnectingEventSource from "reconnecting-eventsource";
export const resetState = () => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.resetState());
    };
};
const coachingFAQs = {
    "Can you explain the concept of Stakeholder Centered Coaching?": {
        "answer": "Stakeholder Centered Coaching is a coaching methodology I developed to help leaders achieve positive, measurable change in behavior, not only for themselves but also within their teams and organizations. It's a process that involves the active participation of stakeholders—those who work with the leader, such as managers, peers, direct reports, and sometimes even family members. These stakeholders provide ongoing feedback and are integral to the leader's development process.\n\nThe process begins with identifying key stakeholders and collecting their perceptions of the leader's strengths and areas for improvement. This feedback is then used to create a focused action plan. The leader selects one or two key behaviors to change and works on these changes with the support of their stakeholders. Regular follow-up and mini-surveys are used to measure progress over a period of 6 months to a year.\n\nThe coaching is centered around three main principles:\n\n1. **Place the Attention and Focus on Stakeholders**: Stakeholders are engaged as active participants in the coaching process, providing feedback and reinforcing the leader's behavioral changes.\n\n2. **Emphasize Feedforward Over Feedback**: While feedback is about past behavior, feedforward focuses on future actions and behaviors. It's about providing suggestions for what can be done differently moving forward.\n\n3. **Change Behavior and Perception in Parallel**: It's important to work on changing not only the leader's behavior but also the stakeholders' perceptions of the leader. This dual focus helps ensure that changes are recognized and sustained.\n\nStakeholder Centered Coaching is unique because it's not just about the leader's self-perception of improvement; it's about real change as observed and reported by those around the leader. It's a powerful approach because it leverages the collective insights and support of those who interact with the leader on a regular basis.",
        "audio_url": "stakeholder_url"
    },
    "What is the most important piece of advice you would give to someone looking to improve their leadership skills?": {
        "answer": "The most important piece of advice I would give to someone looking to improve their leadership skills is to engage in what I call \"feedforward\" instead of feedback. This involves focusing on the future and the positive change that can be made, rather than dwelling on past mistakes. Here are a few key steps:\n\n1. Start with humility: Recognize that you can improve and that others can provide valuable insights into your behavior and leadership style.\n\n2. Identify one behavior to improve: Choose one specific area that you believe will make a significant impact on your leadership effectiveness.\n\n3. Write it down: Clearly articulate your goal for behavioral change and keep it visible to remind yourself daily.\n\n4. Ask for feedforward: Instead of feedback on past behaviors, ask colleagues for their ideas on how you can achieve your goal in the future.\n\n5. Listen and act: Truly listen to the suggestions without judging or critiquing them, and then take action to implement these ideas.\n\n6. Follow up: Regularly check in with those who are helping you to ensure you are making progress and to demonstrate your commitment to change.\n\n7. Show courage and discipline: It takes courage to ask for help and discipline to follow through on making changes, especially when they take you out of your comfort zone.\n\nBy focusing on these steps, you can create a positive path forward and dramatically improve your leadership skills. Remember, the journey to becoming a better leader is a continuous process, not a one-time event.",
        "audio_url": "piece_of_advice_url"
    },
    "What are some key takeaways from your book 'What Got You Here Won't Get You There'?": {
        "answer": "Some key takeaways from my book \"What Got You Here Won't Get You There\" include:\n\n1. **Awareness of Limiting Behaviors**: Recognizing that certain behaviors that have contributed to your past success may become impediments as you seek to achieve higher levels of success.\n\n2. **The Twenty Habits**: Identifying and understanding the twenty workplace habits that can hold you back, such as the need to win at all costs, adding too much value, and passing judgment.\n\n3. **The Importance of Feedback**: Emphasizing the value of honest feedback in understanding how others perceive you, which is crucial for personal growth and professional development.\n\n4. **Apologizing**: The power of a genuine apology to mend bridges and show humility, which can enhance your leadership and interpersonal relationships.\n\n5. **Listening**: The significance of active listening in leadership, which involves being fully present in conversations and showing respect for others' ideas and opinions.\n\n6. **Thanking**: The simple act of expressing gratitude, which can have a profound impact on your work environment and the motivation of your team.\n\n7. **Follow-Up**: The necessity of following up on commitments and the changes you've promised to make, which demonstrates accountability and helps solidify new behaviors.\n\n8. **Feedforward**: Introducing the concept of 'feedforward' as a positive way to focus on future improvements rather than dwelling on past mistakes.\n\n9. **Adaptability**: The need to be flexible and willing to change your approach as you face new challenges and opportunities in your career.\n\n10. **Lifelong Learning**: Encouraging a mindset of continuous learning and self-improvement, recognizing that what has brought you success in one stage of your career will not necessarily be what propels you forward in the next.\n\nThese takeaways are designed to help individuals at all levels of success to continue growing and to avoid the common pitfalls that can prevent them from reaching their full potential.",
        "audio_url": "key_takeaways_url"
    }
};


export const postNewMessage = (
    message: string,
    messageType: string,
    responseMessageType: string,
    chatSessionId: string | null,
    appendMessage: boolean = true,
    isDesktop: string | null,
) => {

    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        try {
            // Clear summary when sending a new message
            dispatch(ChatActions.setSummary(null));

            const { isNewChat } = getState().chatV2Reducer;
            const { selectedLanguage } = getState().globalStateReducer;
            let postData = {
                message: message,
                message_type: messageType, // text | voice
                response_message_type: responseMessageType, // text | voice | video
                username: localStorage.getItem("authenticatedUser"),
                language: selectedLanguage,
                device_type: isDesktop
            };

            if (appendMessage === true) {
                dispatch(
                    ChatActions.appendMessage({
                        sender: "user",
                        message: message,
                        message_type: messageType,
                    }),
                );
            }
            dispatch(ChatActions.setSendMessageApiStatus("inprogress"));
            dispatch(ChatActions.isBotAnswering("inprogress"));
            dispatch(ChatActions.setIsNewChat(false));
            if (coachingFAQs[message]) {
                const chatSessionId = uuidv4();
                dispatch(ChatActions.setAudioMessageId(chatSessionId));
                const botResponse = coachingFAQs[message]['answer'];
                const audio_url = coachingFAQs[message]['audio_url'];
                const messageId = uuidv4();
                const randomDelay = Math.floor(Math.random() * (7000 - 4000 + 1)) + 4000;

                setTimeout(() => {
                    dispatch(
                        ChatActions.updateMessage({
                            sender: "assistant",
                            message: botResponse,
                            message_type: responseMessageType, // Assuming messageType is "text" for the bot's response
                            response_message_type: responseMessageType, // Assuming messageType is "text" for the bot's response
                            chat_session: chatSessionId,
                            feedback: null,
                            feedback_options: [],
                            final: true,
                            audio_url: audio_url,
                            language: "English",
                            message_id: messageId, // Optionally generate a unique ID if required
                            refine_question: message,
                            timestamp: new Date().toISOString(), // Set the current timestamp
                        }),
                    );

                    dispatch(ChatActions.setStreamStarted(1));
                    dispatch(ChatActions.setSendMessageApiStatus("success"));
                    dispatch(ChatActions.setLastMessageDeliveryStatus("delivered"));
                    dispatch(ChatActions.isBotAnswering("success"));
                }, randomDelay);

                return; // Prevent further API calls
            }

            if (chatSessionId === null) {
                const chatSessionIdRequest = ChatService.getNewChatSessionId();
                chatSessionIdRequest
                    .then((response) => {
                        dispatch(
                            ChatActions.setChatSessionId(response.data.sid),
                        );

                        if (responseMessageType === "text") {
                            const postMessageRequest =
                                ChatService.postTextMessage(
                                    response.data.sid,
                                    postData,
                                );
                            postMessageRequest.then((response) => {
                                if (response.data.system_overload) {
                                    dispatch(
                                        ChatActions.setSystemOverload(
                                            response.data.system_overload,
                                            response.data.message,
                                        ),
                                    );
                                }
                                dispatch(
                                    ChatActions.setLastMessageDeliveryStatus(
                                        "delivered",
                                    ),
                                );
                            });
                        } else if (responseMessageType === "voice") {
                            dispatch(ChatActions.setAudioMessageId(response.data.sid));
                            const postMessageRequest = ChatService.postMessage(
                                response.data.sid,
                                postData,
                            );
                            postMessageRequest.then((response) => {
                                if (response.data.system_overload) {
                                    dispatch(
                                        ChatActions.setSystemOverload(
                                            response.data.system_overload,
                                            response.data.message,
                                        ),
                                    );
                                }
                                dispatch(
                                    ChatActions.setLastMessageDeliveryStatus(
                                        "delivered",
                                    ),
                                );
                            });
                        }
                        const sid = response.data.sid;
                        const ctrl = new AbortController();

                        fetchEventDataTextMessage(
                            sid,
                            ctrl,
                            dispatch,
                            getState,
                            responseMessageType,
                        );
                    })
                    .catch((reason: any) => {
                        dispatch(
                            ChatActions.setLastMessageDeliveryStatus("failed"),
                        );
                        dispatch(ChatActions.setSendMessageApiStatus("failed"));
                    });
            } else {
                if (responseMessageType === "text") {
                    const ctrl = new AbortController();
                    const postMessageRequest = ChatService.postTextMessage(
                        chatSessionId,
                        postData,
                    );
                    postMessageRequest.then((response) => {
                        if (response.data.system_overload) {
                            dispatch(
                                ChatActions.setSystemOverload(
                                    response.data.system_overload,
                                    response.data.message,
                                ),
                            );
                        }
                        dispatch(
                            ChatActions.setLastMessageDeliveryStatus(
                                "delivered",
                            ),
                        );
                        
                        dispatch(
                            ChatActions.setImageUrl(response.data.image_url)
                        );
                    });
                    fetchEventDataTextMessage(
                        chatSessionId,
                        ctrl,
                        dispatch,
                        getState,
                        responseMessageType,
                    );
                } else if (responseMessageType === "voice") {
                    const ctrl = new AbortController();
                    const postMessageRequest = ChatService.postMessage(
                        chatSessionId,
                        postData,
                    );

                    postMessageRequest.then((response) => {
                        if (response.data.system_overload) {
                            console.log(response);
                            dispatch(
                                ChatActions.setSystemOverload(
                                    response.data.system_overload,
                                    response.data.message,
                                ),
                            );
                        }
                        dispatch(
                            ChatActions.setLastMessageDeliveryStatus(
                                "delivered",
                            ),
                        );
                        dispatch(
                            ChatActions.setImageUrl(response.data.image_url)
                        );
                    });
                    fetchEventDataTextMessage(
                        chatSessionId,
                        ctrl,
                        dispatch,
                        getState,
                        responseMessageType,
                    );
                    // dispatch(ChatActions.setAudioMessageId(chatSessionId));
                }
            }
            dispatch(ChatActions.isBotAnswering("success"));
        } catch (error) {
            dispatch(ChatActions.setLastMessageDeliveryStatus("failed"));
            dispatch(ChatActions.setSendMessageApiStatus("failed"));
            dispatch(
                GlobalThunks.setNotificationMessage(
                    true,
                    "Message not delivered",
                    "error",
                ),
            );
        }
    };
};

export const submitMessageFeedback = (
    messageId: string,
    action: "TU" | "TD",
    text: string | null,
    checkboxResponses: string[],
) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        try {
            dispatch(ChatActions.setMessageFeedbackApiStatus("inprogress"));
            await ChatService.sendFeedback(
                messageId,
                action,
                text,
                checkboxResponses,
            );
            dispatch(ChatActions.setMessageFeedbackApiStatus("success"));
        } catch (error) {
            dispatch(ChatActions.setMessageFeedbackApiStatus("failed"));
            dispatch(
                GlobalThunks.setNotificationMessage(
                    true,
                    "Failed to save message feedback",
                    "error",
                ),
            );
        }
    };
};

export const setMessageFeedbackApiStatus = (
    status: "inprogress" | "success" | "failed" | null,
) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.setMessageFeedbackApiStatus(status));
    };
};

export const fetchEventDataTextMessage = async (
    sessionId: any,
    ctrl: AbortController,
    dispatch: Dispatch<any>,
    getState: () => RootState,
    responseMessageType?: string,
) => {
    const STREAM_TIMEOUT_MS = 3000; // Auto-close after 3s inactivity
    let streamTimeout: NodeJS.Timeout | null = null;

    const resetStreamTimeout = () => {
        if (streamTimeout) clearTimeout(streamTimeout);
        streamTimeout = setTimeout(() => {
            dispatch(
                ChatActions.setLastMessageDeliveryStatus(
                    "delivered",
                ),
            );
            console.log("⏳ No activity detected. Closing EventSource...");
            eventSource.close();
            dispatch(ChatActions.isBotAnswering("success"));
        }, STREAM_TIMEOUT_MS);
    };

    window.addEventListener("beforeunload", function () {
        if (streamTimeout) clearTimeout(streamTimeout);
        ctrl.abort();
    });

    const eventSource = new ReconnectingEventSource(
        `${process.env.REACT_APP_API_BASE_URL}events/${sessionId}`,
    );

    eventSource.onopen = (response: any) => {
        console.log("✅ EventSource opened");
        dispatch(ChatActions.setStreamStarted(1));
        
        if (
            response.status >= 400 &&
            response.status < 500 &&
            response.status !== 429
        ) {
            console.log("❌ EventSource client error");
            eventSource.close();
        }
    };

    eventSource.onmessage = (event) => {
        if (event.data) {
            dispatch(ChatActions.isBotAnswering("inprogress"));
            const edata = JSON.parse(event.data);
            if (responseMessageType === "voice") {
                console.log("Voice message triggered");
                dispatch(ChatActions.setAudioMessageId(sessionId));
            }
            
            if (
                edata["text"] &&
                edata["type"] === "text" &&
                edata.sequence !== "END"
            ) {
                resetStreamTimeout(); // reset on each message
                // console.log("📨 Text message: ", edata["text"]['message_id'], ':', edata["text"]['message']);
                const currentState = getState().chatV2Reducer;
                if (sessionId === currentState.chatSessionId) {
                    console.log("*****************************************",edata["text"]);
                    dispatch(ChatActions.updateMessage(edata["text"]));
                    dispatch(ChatActions.setImageUrl(edata["text"].image_url));
                    dispatch(ChatActions.setSendMessageApiStatus("success"));
                }
            }
            if (edata.sequence === "END") {
                console.log("✅ Event stream ended, closing");
                eventSource.close();
                if (streamTimeout) clearTimeout(streamTimeout);
                dispatch(ChatActions.isBotAnswering("success"));
                dispatch(
                    ChatActions.setLastMessageDeliveryStatus(
                        "delivered",
                    ),
                );
            }
        } else {
            console.log("event.data is empty");
        }
    };

    eventSource.onerror = (err) => {
        console.log("EventSource error", err);
        eventSource.close();
        if (streamTimeout) clearTimeout(streamTimeout);
        dispatch(ChatActions.setSendMessageApiStatus("failed"));
        if (err) throw err;
    };
};


export const setChatType = (type: "text" | "voice" | "video") => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.setChatType(type));
    };
};

export const setIsNewChat = (type: true | false) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.setIsNewChat(type));
    };
};

export const setChatTypeForUpdateMessage = (type: "voice" | "text") => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.setChatTypeForUpdateMessage(type));
    };
};

export const restartConversation = (restart: boolean) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.setRestartConversation(restart));
    };
};

export const clearVideoUrls = (messageId: string) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        dispatch(ChatActions.clearVideoUrls(messageId));
    };
};

export const textToVideo = (message: Record<any, any>) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        try {
            dispatch(ChatActions.setSendMessageApiStatus("inprogress"));
            const response = await ChatService.textToVideo(message.message_id);
            // console.log(response.data.text);
            dispatch(ChatActions.appendMessage(message));
            dispatch(ChatActions.setSendMessageApiStatus("success"));

            if (
                response.data.text &&
                Array.isArray(response.data.text) &&
                response.data.text.length > 0
            ) {
                const videoUrls = response.data.text.map((videoBytes) => {
                    const byteCharacters = window.atob(videoBytes);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const videoBlob = new Blob([byteArray], {
                        type: "video/mp4",
                    });
                    const videoUrl = URL.createObjectURL(videoBlob);
                    return videoUrl;
                });

                dispatch(
                    ChatActions.updateMessageIdVideoUrls(
                        message.message_id,
                        videoUrls,
                    ),
                );
            }
        } catch (error) {
            console.log(error);
        }
    };
};

export const retryLastFailedMessage = () => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        try {
            const currentState = getState().chatV2Reducer;
            const userMessages = currentState.messages.filter(
                (item: Record<any, any>) => item.sender === "user",
            );

            if (userMessages.length > 0) {
                const lastMessage = userMessages[userMessages.length - 1];
                dispatch(
                    postNewMessage(
                        lastMessage.message,
                        lastMessage.chat_type,
                        lastMessage.chat_input_type,
                        currentState.chatSessionId,
                        false,
                        lastMessage.isDesktop
                    ),
                );
            }
        } catch (error) {
            console.log(error);
        }
    };
};

export const handleFetchMessages = (chatId: string, fetchFromStoreOnly: boolean = false) => {
    return async (dispatch: Dispatch<any>, getState: () => RootState) => {
        try {
            dispatch(ChatActions.fetchMessagesRequest());
            let messages = []

            if (fetchFromStoreOnly) {
                const currentState = getState().chatV2Reducer;
                messages = currentState.messages;
                console.log('currentState.messages', currentState.messages)
            } else {
                const response = await ChatHistoryService.fetchSessionMessages(
                    chatId,
                );
                messages = response.data;

                // Fetch summary separately
                try {
                    const token = localStorage.getItem("authenticationToken");
                    const summaryResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/chat/${chatId}/summary/`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });
                    if (summaryResponse.ok) {
                        const summaryData = await summaryResponse.json();
                        const summary = summaryData?.summary;
                        if (summary) {
                            dispatch(ChatActions.setSummary(summary));
                        }
                    }
                } catch (error) {
                    console.error("Error fetching summary:", error);
                }
            }


            dispatch(ChatActions.fetchMessagesSuccess(chatId, messages));
        } catch (error) {
            dispatch(ChatActions.fetchMessagesFailure(error));
            console.error(`Error fetching messages for chat ${chatId}:`, error);
        }
    };
};
